import { ArrowUpRight, Sunrise, HeartPulse } from "lucide-react";
import { testIds } from "@/lib/api";

const HERO_IMG =
  "https://images.unsplash.com/photo-1542338008-e7cbcf59c0a8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxwZXJzb25hbCUyMHRyYWluZXIlMjBvdXRkb29ycyUyMHN1bnNldHxlbnwwfHx8fDE3ODI5NjY5MTl8MA&ixlib=rb-4.1.0&q=85";

export default function Hero() {
  return (
    <section id="top" className="relative pt-40 pb-24 md:pt-48 md:pb-32 px-6 md:px-12 overflow-hidden">
      <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-10 md:gap-16 items-end">
        {/* Left: type-forward hero */}
        <div className="md:col-span-7 reveal">
          <a
            href="/journey"
            data-testid="hero-journey-pill"
            className="group inline-flex items-center gap-2 rounded-full border border-[#C57B57]/40 bg-[#C57B57]/10 px-4 py-1.5 text-sm text-[#AD6A48] font-medium mb-6 hover:bg-[#C57B57]/20 transition-colors"
          >
            <span className="text-xs uppercase tracking-wider bg-[#AD6A48] text-white rounded-full px-2 py-0.5">New</span>
            AI Journey Builder — plan your India wellness journey
            <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <div className="flex items-center gap-3 mb-6">
            <span className="overline">A Personal Training Practice</span>
            <span className="h-px w-10 bg-[#89937C]" />
          </div>
          <h1
            data-testid={testIds.hero.title}
            className="font-serif-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-normal leading-[1.05] tracking-tight text-[#2D372B]"
          >
            Train the body.
            <br />
            <span className="italic text-[#AD6A48]">Steady</span> the mind.
            <br />
            Nourish the whole.
          </h1>
          <p className="mt-8 text-lg md:text-xl leading-relaxed text-[#5C665A] max-w-xl">
            Small-group bootcamps and 1:1 coaching that treat physical strength,
            mental wellness, and nutrition as one continuous practice — not
            three disconnected checkboxes.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href="#bootcamps"
              data-testid={testIds.hero.ctaPrimary}
              className="group inline-flex items-center gap-2 rounded-full px-8 py-4 bg-[#89937C] text-white font-medium hover:bg-[#737D67] transition-all duration-300"
            >
              Explore Bootcamps
              <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              href="#about"
              data-testid={testIds.hero.ctaSecondary}
              className="inline-flex items-center gap-2 rounded-full px-8 py-4 border border-[#2D372B]/20 text-[#2D372B] font-medium hover:border-[#2D372B] transition-colors"
            >
              Meet the Coach
            </a>
          </div>

          <a
            href="/wellness-quiz"
            data-testid="hero-wellness-quiz-link"
            className="group mt-6 inline-flex items-center gap-2.5 text-[#AD6A48] font-medium hover:text-[#8A5236] transition-colors"
          >
            <span className="w-9 h-9 rounded-full bg-[#AD6A48]/10 flex items-center justify-center group-hover:bg-[#AD6A48]/20 transition-colors">
              <HeartPulse size={16} />
            </span>
            Take the free wellness assessment
            <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>

          <div className="mt-14 flex items-center gap-8 text-sm text-[#5C665A]">
            <div>
              <div className="font-serif-display text-3xl text-[#2D372B]">12+ yrs</div>
              coaching experience
            </div>
            <div className="h-10 w-px bg-[#E5E2D9]" />
            <div>
              <div className="font-serif-display text-3xl text-[#2D372B]">400+</div>
              lives transformed
            </div>
          </div>
        </div>

        {/* Right: asymmetric image */}
        <div className="md:col-span-5 relative reveal" style={{ animationDelay: "150ms" }}>
          <div className="relative aspect-[3/4] rounded-3xl overflow-hidden bg-[#E5E2D9]">
            <img
              src={HERO_IMG}
              alt="Coach Sanjay outdoors at sunset"
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 ring-1 ring-inset ring-black/5 rounded-3xl" />
          </div>

          <div className="absolute -bottom-6 -left-6 bg-white border border-[#E5E2D9] rounded-2xl px-5 py-4 shadow-sm flex items-center gap-3 max-w-[260px]">
            <div className="w-10 h-10 rounded-full bg-[#F7F5F0] flex items-center justify-center">
              <Sunrise size={18} className="text-[#AD6A48]" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-[#89937C] font-semibold">
                Now enrolling
              </div>
              <div className="text-sm text-[#2D372B]">Sunrise Strength · Feb 10</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
